import { useCallback, useState } from "react";

import { Close, Create } from "@mui/icons-material";
import { Box, Link, Stack, Typography } from "@mui/material";

import { Button } from "@covertree/ui";
import { formatCurrency } from "@covertree/utils";

import {
  ModalTitleForValuables,
  PersonalPropertyWithPrice,
} from "./ScheduledPersonalPropertyCard";
import { AddOrEditPersonalProperty } from "./AddOrEditPersonalProperty";

interface SchedulePersonalPropertyModalContentProps {
  addOrUpdateItem(value: PersonalPropertyWithPrice): void;
  closeModal(): void;
  currentPriceOfAllItems: number;
  deleteItem(
    personalProperty: PersonalPropertyWithPrice & { id: string }
  ): number;
  fields: (PersonalPropertyWithPrice & { id: string })[];
  readonly?: boolean;
  updateModalTitle(title: ModalTitleForValuables): void;
}

export function SchedulePersonalPropertyModalContent({
  addOrUpdateItem,
  closeModal,
  currentPriceOfAllItems,
  deleteItem,
  fields,
  readonly = false,
  updateModalTitle,
}: SchedulePersonalPropertyModalContentProps) {
  const [isFormOpen, setIsFormOpen] = useState<boolean>(!fields.length);
  const [selectedItem, setSelectedItem] = useState<
    (PersonalPropertyWithPrice & { id: string }) | undefined
  >();

  const handleEdit = useCallback(
    (item: PersonalPropertyWithPrice & { id: string }) => {
      if (readonly) return;
      setSelectedItem(item);
      setIsFormOpen(true);
      updateModalTitle(ModalTitleForValuables.Initial);
    },
    [readonly, updateModalTitle]
  );

  const handleAddNewItem = useCallback(() => {
    setSelectedItem(undefined);
    setIsFormOpen(true);
    updateModalTitle(ModalTitleForValuables.Initial);
  }, [updateModalTitle]);

  const handleRemove = useCallback(
    (item: PersonalPropertyWithPrice & { id: string }) => {
      if (readonly) return;

      const newLength = deleteItem(item);
      if (newLength === 0) {
        setIsFormOpen(true);
        updateModalTitle(ModalTitleForValuables.Initial);
      } else {
        updateModalTitle(ModalTitleForValuables.ValuableRemoved);
      }
    },
    [deleteItem, readonly, setIsFormOpen, updateModalTitle]
  );

  const changeFormVisibilityAndCloseModal = useCallback(
    (shouldClose: boolean) => {
      if (fields.length === 0 && shouldClose) {
        closeModal();
      }
      setIsFormOpen(false);
    },
    [closeModal, fields, setIsFormOpen]
  );

  return (
    <Box width="100%">
      {isFormOpen ? (
        <AddOrEditPersonalProperty
          addOrUpdateItem={addOrUpdateItem}
          changeFormVisibilityAndCloseModal={changeFormVisibilityAndCloseModal}
          currentItem={selectedItem}
          updateModalTitle={updateModalTitle}
        />
      ) : (
        <>
          <Box mb={5}>
            <Typography variant="h5">
              The total annual premium increased by{" "}
              {formatCurrency(currentPriceOfAllItems)}.
              <br /> Are you sure you want to apply the following changes?
            </Typography>
          </Box>

          <Stack spacing={3} m={0}>
            {fields.map((item) => (
              <Box
                sx={(theme) => ({
                  width: "100%",
                  minHeight: theme.spacing(7),
                  backgroundColor: "secondary.contrastText",
                  padding: theme.spacing(2, 2),
                  borderRadius: theme.spacing(1.5),
                })}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                key={item.id ?? item.fieldGroupLocator}>
                <Typography
                  variant="h5"
                  sx={{
                    overflowWrap: "break-word",
                    maxWidth: "70%",
                  }}>
                  {item.description}{" "}
                  <Typography
                    variant="h5"
                    component="span"
                    sx={(theme) => ({
                      display: "inline-block",
                      color: theme.colors.brown,
                      marginLeft: theme.spacing(1),
                    })}>
                    {formatCurrency(item.value)}
                  </Typography>
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Box>
                    <Typography variant="h5" color="secondary">
                      +{formatCurrency((item.price ?? 0) / 12, 2)}/mo
                    </Typography>
                    <Typography variant="subtitle1" ml={1.5}>
                      ({formatCurrency(item.price ?? 0, 2)}/year)
                    </Typography>
                  </Box>
                  <Link
                    component="button"
                    variant="body2"
                    underline="always"
                    data-testid="edit-personal-property-button"
                    sx={(theme) => ({
                      marginTop: theme.spacing(0.5),
                      color: "info.main",
                    })}
                    onClick={() => handleEdit(item)}>
                    <Create fontSize="inherit" />
                  </Link>
                  <Link
                    component="button"
                    variant="body2"
                    underline="always"
                    data-testid="remove-personal-property-button"
                    sx={(theme) => ({
                      marginTop: theme.spacing(0.5),
                      color: "info.main",
                    })}
                    onClick={() => handleRemove(item)}>
                    <Close fontSize="inherit" />
                  </Link>
                </Stack>
              </Box>
            ))}
          </Stack>

          <Box mt={3} mb={4}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddNewItem}
              data-testid="add-another-item-button">
              Add another item
            </Button>
          </Box>

          <Box>
            <Button onClick={() => closeModal()} data-testid="finish-button">
              Finish
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
